import { IAIAssistantExtension } from './assistant-extension';
import { getAiFloatComponentFromDC } from '@src/slide/application/service/ai-float-manager/get-ai-component';
import { ESource } from '@tencent/ai-sdk-interface';
import { DOMId } from '@editor/src/page/index/components/canvas/canvas-constants';
import { MODE, SlideContract } from '@src/slide/application/service/ai-float-manager/slide-contract';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    SelectedModelType,
} from '@src/slide/application/service/selection/selection-service.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { STATUS_BAR_HEIGHT } from '@editor/src/js/constants';
import {
    AI_GENERATE_PAGE_COMMAND_ID,
    getSoftKeyboardHeightInAiFloat,
} from '@src/slide/application/service/ai-float-manager/const';
import {
    AiWritingSourceType,
    IAIFloatManager,
    IAiFloatManagerDelegate,
    IInitAiFloatComponent,
} from '@src/slide/application/service/ai-float-manager/ai-manager.interface';
import {
    IMobileToolbarEditManager,
} from '@src/slide/application/service/mobile-toolbar-edit-manager/core/mobile-toolbar-edit-manager.interface';
import { observable } from 'mobx';
import ua from '@tencent/docs-user-agent';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';

const MAX_CONTAINER_HEIGHT = 432;
const MAX_CONTAINER_WIDTH = 565;


export default class AiFloatManager implements IAIFloatManager {
    @observable
    public isOpen = false; // isShow
    @observable
    public heightInCanvas = 0; // 参与 canvas 画布布局计算的高度
    private aiFloatComponent: IAIAssistantExtension | null;
    private maskLayerContainer: HTMLDivElement | undefined;
    private positionContainer: HTMLDivElement | undefined;
    private contract: SlideContract;
    private context: any;
    private aiFlotResizeObserver: ResizeObserver;
    private services: IAiFloatManagerDelegate;
    private mobileMountDom: HTMLDivElement | undefined; // 移动端 ai float mounted 以后的元素
    private isFirstShow = true;  // 是否第一 show
    private hasFixNoFocusProblem = false; // 是否已经解决第一次进入小程序后，点击 text_value 不聚焦的问题
    private isInputFocus = false;

    public constructor(props: IAiFloatManagerDelegate) {
        this.services = props;
    }

    /**
     * 打开pc端ai组件
     * @param props
     */
    public async showAIModal(props: IInitAiFloatComponent) {
        this.aiFlotResizeObserver?.disconnect();
        this.createPcContainer(!!props.positionWithSelection);
        const aiFloatComponent = await this.getAiFloatComponent(props);
        let showParams;
        if (props.directShowPPTGeneration) {
            showParams = {
                commandID: AI_GENERATE_PAGE_COMMAND_ID,
                doNotChat: true,
            };
        }

        aiFloatComponent?.show(showParams);
    }

    public setDelegate(mobileToolbarEditManager: IMobileToolbarEditManager) {
        this.services.mobileToolbarEditManager = mobileToolbarEditManager;
    }

    /**
     * 打开mobile端ai组件
     * @param props
     */
    public async showMobileAIModal(props: IInitAiFloatComponent) {
        const editPanelShow = this.services.mobileToolbarEditManager?.getEditPanelShowState();

        // 获取到当前正在编辑的 TextEditor 实例
        if (editPanelShow) {
            this.services?.mobileToolbarEditManager?.closeEditPanel();
        }

        // 取消监听 FlotResizeObserver ，保证不重复监听
        this.aiFlotResizeObserver?.disconnect();

        // 获取组件
        const aiFloatComponent = await this.getAiFloatComponent(props);

        // 状态更新
        this.isOpen = true;

        // 创建/更新蒙层
        if (!this.maskLayerContainer) {
            this.maskLayerContainer = this.createMaskLayer();
            document.body.appendChild(this.maskLayerContainer);
        } else {
            this.maskLayerContainer.style.display = 'block';
        }

        // 添加横屏竖屏监听
        window.addEventListener('orientationchange', this.handleOrientationchange);

        // 添加滚动监听，若发生滚动，则滚动回顶部
        window.addEventListener('scroll', this.onMobileScroll);

        // 设置 show 参数，执行 show
        let showParams;
        if (props.directShowPPTGeneration) {
            showParams = {
                commandId: AI_GENERATE_PAGE_COMMAND_ID,
                doNotChat: true,
            };
        }
        aiFloatComponent?.show(showParams);
    }

    /**
     * 隐藏ai组件
     */
    public async hideAiModal(params?: { ignoreCompleteTip: boolean }) {
        const aiFloatComponent = await this.getAiFloatComponent();
        aiFloatComponent?.hide(params);
    }

    public async triggerGeneratePage(props: IInitAiFloatComponent) {
        await this.showAIModal({ ...props, directShowPPTGeneration: true });
    }

    public async triggerGeneratePageMobile(props: IInitAiFloatComponent) {
        await this.showMobileAIModal({ ...props, directShowPPTGeneration: true });
    }

    /*  FIXME 和 getEditorSelectionText 有重复代码 */
    public getSourceType(): AiWritingSourceType | '' {
        const element = this.getSelectedModel();

        if (!element || !this.isShapeModel(element)) {
            return '';
        }

        const shapeModel = element as IShapeModel;
        const { editorInstance } = shapeModel;
        if (!editorInstance) return '';

        if (editorInstance.isExpanded()) {
            return AiWritingSourceType.textExpanded;
        } if (editorInstance.isEditing) {
            return AiWritingSourceType.textEditing;
        }

        return AiWritingSourceType.element;
    }

    /**
     * 获取ai组件，初始化/更新参数
     * @param options
     * @private
     */
    private async getAiFloatComponent(options?: IInitAiFloatComponent): Promise<IAIAssistantExtension | null> {
        if (!this.aiFloatComponent) {
            options && await this.init(options);
        } else {
            options && this.updateOptions(options);
        }
        return this.aiFloatComponent;
    }

    /**
     * 更新参数
     * @param options
     * @private
     */
    private updateOptions(options: IInitAiFloatComponent) {
        const { directShowPPTGeneration } = options;
        this.contract.setMode(directShowPPTGeneration ? MODE.generatingPage : MODE.aiWithElement);
        this.context.clientVars.directShowPPTGeneration = directShowPPTGeneration;
        this.context.getSelectedContent = directShowPPTGeneration ? () => '' : () => this.getEditorSelectionText();
    }

    /**
     * 初始化 docs-ai 组件
     * @param props
     * @returns 返回组件
     */
    private async init(props: IInitAiFloatComponent) {
        const { directShowPPTGeneration } = props;
        const aiFloatComponent = await getAiFloatComponentFromDC();
        this.contract = new SlideContract({
            services: this.services,
            isGeneratingPage: directShowPPTGeneration,
            updateCanvasHeight: this.updateCanvasHeight,
        });

        this.context = {
            clientVars: {
                ...window.clientVars,
                directShowPPTGeneration,
            },
            getSelectedContent: directShowPPTGeneration ? () => '' : () => this.getEditorSelectionText(),
        };

        aiFloatComponent.initialize({
            source: ESource.SOURCE_SLIDE,
            context: this.context,
            lifecycles: {
                onBeforeWriting: () => {
                },
                onClose: () => {
                    this.isOpen = false;
                    this.heightInCanvas = 0;
                    this.isFirstShow = false;

                    if (this.maskLayerContainer) {
                        this.maskLayerContainer.style.display = 'none';
                    }

                    this.contract.onModalClose?.();

                    this.aiFlotResizeObserver?.disconnect();

                    // 移除移动端的监听
                    window.removeEventListener('orientationchange', this.handleOrientationchange);
                    window.removeEventListener('scroll', this.onMobileScroll);
                },
                onMounted: (mountDom) => {
                    if (ua.isMobile) {
                        if (!this.isOpen) return;
                        this.mobileMountDom = mountDom;
                        this.contract.setMountDom(mountDom);
                        // 移动端浮窗 mount 后如果 heightInCanvas 为 0，说明并没有 updateMobileHeight，强制执行一次
                        if (!this.heightInCanvas) {
                            this.updateMobileHeight(true);
                        }
                    }
                },
                onInputFocus: () => this.handleAiFloatInputFocus(),
                onInputBlur: () => this.handleAiFloatInputBlur(),
                onInputClick: () => this.handleAiFloatInputClick(),
            },
            uiConfig: {
                boardOnly: true,
                embedMode: true,
                getEmbedNode: () => document.querySelector(`#${DOMId.AI_MODAL}`)!,
            },
            contract: this.contract,
        });

        this.aiFloatComponent = aiFloatComponent;
        return this.aiFloatComponent;
    }

    private isShapeModel(model: SelectedModelType): boolean {
        return model?.type === UIModelType.SHAPE;
    }

    private getSelectedModel(): SelectedModelType {
        return this.services.selectionService.getSelectedModel();
    }

    /**
     * 获取选取文字
     * 如果是选中形状，则返回拼接的所有textRun
     * 如果是选中的选区，则返回选区内容
     * 如果是光标态，则返回光标所在段落
     * @private
     */
    private getEditorSelectionText(): string {
        const element = this.getSelectedModel();

        if (!element || !this.isShapeModel(element)) {
            return '';
        }

        const shapeModel = element as IShapeModel;
        const { editorInstance } = shapeModel;
        if (!editorInstance) return '';

        // 非编辑态，返回全部textRun拼接字符
        if (!editorInstance.isEditing) {
            return shapeModel.getTextContent() || '';
        }

        // 选区态，返回选区文本
        if (editorInstance.isExpanded()) {
            return editorInstance.editor.getString() || '';
        }

        // 光标态，返回光标所在段落
        const selection = editorInstance.getSelection();
        if (selection?.focus?.path) {
            const [, focusParagraphPath] = editorInstance.editor.getParent(selection.focus.path);
            const focusParagraphText = editorInstance.editor.getString(focusParagraphPath);
            return focusParagraphText || '';
        }

        return '';
    }

    /**
     * 创建蒙层
     */
    private createMaskLayer = () => {
        if (!this.maskLayerContainer) {
            this.maskLayerContainer = document.createElement('div');
            this.maskLayerContainer.style.width = '100%';
            this.maskLayerContainer.style.height = '100%';
            this.maskLayerContainer.style.position = 'absolute';
            this.maskLayerContainer.style.left = '0';
            this.maskLayerContainer.style.top = '0';
            this.maskLayerContainer.style.zIndex = '999';
            this.maskLayerContainer.style.overflow = 'hidden';
            this.maskLayerContainer.onmousedown = () => {
                this.hideAiModal();
            };
            ua.isMobile && this.maskLayerContainer.addEventListener('touchmove', (event) => {
                event.preventDefault();
                event.stopPropagation();
            }, { passive: false });
        }

        return this.maskLayerContainer;
    };

    /**
     * 创建组件定位容器
     */
    private createPositionContainer = (positionWithSelection: boolean) => {
        if (!this.positionContainer) {
            this.positionContainer = document.createElement('div');
            this.positionContainer.onmousedown = (e) => {
                if (e.target === this.positionContainer) {
                    return;
                }

                e.stopPropagation();
            };

            this.positionContainer.id = DOMId.AI_MODAL;
            this.positionContainer.style.position = 'absolute';
            this.positionContainer.style.zIndex = '1000';
            this.updatePositionContainer(positionWithSelection);
        }

        return this.positionContainer;
    };

    /**
     * 更新定位容器
     * @param positionWithSelection
     */
    private updatePositionContainer = (positionWithSelection: boolean) => {
        const selector = document.querySelector('#selected-element-outer');
        if (!this.positionContainer) return;
        if (positionWithSelection && selector) {
            const selectorRect = selector.getBoundingClientRect();
            const viewHeight = document.documentElement.clientHeight;
            const viewWidth = document.documentElement.clientWidth;
            let top = selectorRect.bottom + 10;
            let left = selectorRect.left + (selectorRect.width / 2) - (MAX_CONTAINER_WIDTH / 2);

            if (top + MAX_CONTAINER_HEIGHT > viewHeight) {
                const yOffset = top + MAX_CONTAINER_HEIGHT - viewHeight;
                top = top - yOffset;
            }

            if (left < 0) {
                left = 0;
            } else if (left + MAX_CONTAINER_WIDTH > viewWidth) {
                const xOffset = left + MAX_CONTAINER_WIDTH - viewWidth;
                left = left - xOffset;
            }

            this.positionContainer.style.top = `${top}px`;
            this.positionContainer.style.left = `${left}px`;
            this.positionContainer.style.width = `${MAX_CONTAINER_WIDTH}px`;
            this.positionContainer.style.height = `${MAX_CONTAINER_HEIGHT}px`;
            this.positionContainer.style.transform = '';
        } else {
            this.positionContainer.style.top = '50%';
            this.positionContainer.style.left = '50%';
            this.positionContainer.style.width = `${MAX_CONTAINER_WIDTH}px`;
            this.positionContainer.style.height = `${MAX_CONTAINER_HEIGHT}px`;
            this.positionContainer.style.transform = 'translate(-50%, -50%)';
        }
    };

    /**
     * 创建ai浮层容器，并挂载ai组件
     * @param positionWithSelection 是否跟随选择框展示
     * @private
     */
    private createPcContainer(positionWithSelection: boolean) {
        if (!this.maskLayerContainer) {
            this.maskLayerContainer = this.createMaskLayer();
            this.positionContainer = this.createPositionContainer(positionWithSelection);
            this.maskLayerContainer.appendChild(this.positionContainer);
            document.body.appendChild(this.maskLayerContainer);

            // 监听 ai-float-modal 尺寸变化的监听器
            this.initComponentSizeObserver();

            // 监听 this.positionContainer 子节点数量变化
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList') {
                        const element = mutation.target as HTMLDivElement;
                        // 监听子节点数量变化，如果子节点数量大于0，则说明 ai-float-modal 已经显示
                        const childNodesNum = element.children.length;
                        if (childNodesNum > 0) {
                            // 监听 ai-float-modal 的尺寸变化
                            this.aiFlotResizeObserver.observe(element.children[0]);
                        }
                    }
                });
            });

            observer.observe(this.positionContainer, {
                childList: true, // 监听子节点数量变化
            });
        } else {
            this.maskLayerContainer.style.display = 'block';
            if (!this.positionContainer) return;
            this.updatePositionContainer(positionWithSelection);
        }
    }

    /**
     * 创建ai组件尺寸监听，用于动态调整容器位置
     * @private
     */
    private initComponentSizeObserver() {
        this.aiFlotResizeObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                // element 为 ai-float-modal
                const element = entry.target as HTMLDivElement;
                const { top, bottom } = element.getBoundingClientRect();
                if (this.maskLayerContainer && this.positionContainer) {
                    // 获取 ai-float-modal 最下半部分 距离页面底部的距离
                    const distanceToBottom = this.maskLayerContainer.clientHeight - bottom;
                    if (distanceToBottom < 0) {
                        this.positionContainer.style.top = `${top + distanceToBottom - STATUS_BAR_HEIGHT}px`;
                    }
                }
            });
        });
    }

    /**
     * 移动端处理ai浮窗 focus 时的函数
     * onInputFocus
     * @private
     */
    private async handleAiFloatInputFocus() {
        this.isInputFocus = true;

        if (ua.isMobile) {
            if (!this.isOpen) return;
            this.updateMobileHeight(true);
        }
    }

    private updateMobileHeight = async (isNeedSoftBoard = true) => {
        const dom = this.mobileMountDom?.children?.[0] as HTMLDivElement;
        if (dom) {
            const height = isNeedSoftBoard ? await getSoftKeyboardHeightInAiFloat() : 0;
            if (!this.isOpen) {
                this.heightInCanvas = 0;
                return;
            }
            if (ua.isIOS) {
                dom.style.bottom = `${height}px`;
                dom.style.position = 'absolute';
            }
            this.heightInCanvas = height + dom.clientHeight;
        } else {
            this.heightInCanvas = 0;
        }
    };

    /**
     * 移动端处理ai浮窗 blur 时的函数
     * @private
     */
    private handleAiFloatInputBlur() {
        this.isInputFocus = false;
        if (ua.isMobile) {
            this.updateMobileHeight(false);
        }
    }

    private handleAiFloatInputClick() {
        // fix：移动端小程序第一次 show 后，点击 “text_value”（如默认的"陶渊明的故事"）不会触发 handleAiFloatInputFocus 的问题
        // (可能是由 微信、H5 第一次 show 时键盘不会被唤起所导致)
        if (ua.isMobile) {
            if (browser.isMiniProgram) {
                !this.hasFixNoFocusProblem && this.isFirstShow && this.handleAiFloatInputFocus();
                this.hasFixNoFocusProblem = true;
            }
        }
    }

    private updateCanvasHeight = (height: number) => {
        this.heightInCanvas = height;
    };

    private handleOrientationchange = () => {
        if (ua.isMobile) {
            this.isOpen && this.updateMobileHeight(this.isInputFocus);
        }
    };

    private onMobileScroll = () => {
        this.isOpen && window.scrollY && window.scrollTo(0, 0);
    };
}
